import { DeltaHedgeOrderType } from "./deltaHedgeOrder";
import { Graph_Dto } from "./orderbookGraphInterfaces";
import { StepOrderSide } from "./stepInterfaces";
import { ISearchParameters, ITableReport, Report } from "./TableInterfaces";

export interface IOptionDomainFormValues {
  id?: number;
  baseInsId: string;
  volatility: number;
  dividendYield: number;
  remainingDayMax: number;
  minLastTradeVolume: number;
  avg30Bubble: number;
  std30Bubble: number;
  roundingFactor: number;
  groupId: number[];
  minCalculationBubble: number;
}

export interface IOptionDomainSearchParam extends ISearchParameters {
  baseIns: number;
  insId: number;
}

export interface IOptionDomainLiveWatchListSearchParam extends ISearchParameters {
  baseInsId: number;
  insId: number;
}
export interface IOptionDomainPortfoWatchListSearchParam extends ISearchParameters {
  baseInsId: number;
  insId: number;
}

export interface IOptionDomainSearchParams extends ISearchParameters {
  insId: number;
  baseIns: number;
  baseInsId: number;
  groupId: number;
  positionType: number;
}

export interface IOptionDomainTableReport extends ITableReport {
  baseInstrument: string;
  baseInsId: string;
  volatility: string;
  dividendYield: string;
  avg30Bubble: string;
  std30Bubble: string;
  roundingFactor: string;
  group: string;
  groupId: number[];
  remainingDayMax: string;
  minLastTradeVolume: string;
  minCalculationBubble: number;
}
export interface IGroupTableReport extends ITableReport {
  name: string;
  fullName: string;
  instruments: string[];
}

export interface IWhiteListTableReport extends ITableReport {
  mainInstrumentId: string;
  mainInstrumentName: string;
  optionInstrumentNames: string[];
}

export interface IOptionDomainLiveWatchListTableResp extends Report<IOptionDomainTableReport> {
  activeNumber: number;
}

export interface IGetRiskFreeRateResp {
  riskFreeRate: number;
  volatilityIndicator: number;
}

export interface IGroupAddEditDTO {
  id: number;
  name: string;
  instruments: string[];
}

export enum PositionType {
  CS = 1,
  PS,
  LS,
  SS,
  CC,
  PP,
  RCC,
  RPP,
}

export interface IOptionDomainChartsReport extends ITableReport {
  point: number;
  dailyLockedProfitPortfolio: number;
  gamaPercent: number;
  deltaPercent: number;
  isCurrent: boolean;
}
export interface IPairPositionChartSearchParams {
  from: number;
  to: number;
  pointCount: number;
  chartType: number;
  ins1: string;
  ins2: string;
  positionType1: string;
}
export interface IPairPositionSearchParams {
  positionType: string[];
  groupId: number[];
  // sameDueDate: boolean;
  remainingOptionId: number;
  sameKind: boolean;
  sameStrikePrice: boolean;
  ins2: string;
  ins1: string;
  baseIns2: string;
  baseIns1: string;
  maxProfitLock: number;
  minProfitLock: number;
  maxReHedgeRatio: number;
  minLastDayTradeVolume: number;
  minLastDayTradeValue: number;
  lessMinRemainingDay: number;
  lessMaxRemainingDay: number;
  moreMinRemainingDay: number;
  moreMaxRemainingDay: number;
  minHedgeFactor: number;
  maxHedgeFactor: number;
  side1MinPrice: string;
  side2MinPrice: string;
  side1MinBasePricePercentage: string;
  side2MinBasePricePercentage: string;
  minGamma: string;
  maxGamma: string;
}

export interface IPairPositionReport extends ITableReport {
  openOrderExpr: string;
  openOrder: string;
  priceValue: string;
  instrumentName1: string;
  instrumentId1: string;
  orderSide1: string;
  instrumentName2: string;
  instrumentId2: string;
  orderSide2: string;
  orderSideValue1: StepOrderSide;
  orderSideValue2: StepOrderSide;
  positionType1: string;
  group: string;
  optionType1: DeltaHedgeOrderType;
  optionType2: DeltaHedgeOrderType;
  volume1: string;
  volume2: string;
  yesterDayVolume1Str: string;
  yesterDayVolume1: number;
  yesterDayVolume2Str: string;
  yesterDayVolume2: number;
  lastPrice1: string;
  lastPrice2: string;
  blackScholes1: string;
  blackScholes2: string;
  baseLastPrice1: string;
  baseLastPrice2: string;
  remainingDay1: string;
  remainingDay2: string;
  contractSize1: string;
  contractSize2: string;
  volatilityBaseIns1: string;
  volatilityBaseIns2: string;
  relativeTurbulence1: string;
  relativeTurbulence2: string;
  delta1: string;
  delta2: string;
  gama1: string;
  gama2: string;
  iv1: string;
  iv2: string;
  indicatorDeltaPercentage1: string;
  indicatorDeltaPercentage2: string;
  indicatorGamaPercentage1: string;
  indicatorGamaPercentage2: string;
  gamaInTermsOfDeltaPercentage1: string;
  gamaInTermsOfDeltaPercentage2: string;
  dailyLockProfit1: string;
  dailyLockProfit2: string;
  involvedFund1: string;
  involvedFund2: string;
  normalBubble1: string;
  normalBubble2: string;
  bubble1: string;
  bubble2: string;
  hedgeFactor: string;
  remainingDayStr: string;
  remainingDay: number;
  dailyLockProfit: string;
  involvedFund: string;
  dailyLockProfitPercentStr: string;
  dailyLockProfitPercent: number;
  gamaInTermsOfDeltaPercentage: string;
  gamaPercent: string;
  hedgeFactorChange: string;
  relativeNormalBubble: string;
  absBubbleDiff: string;
}

export interface IGetOrdersDTO {
  accountIds: number[];
  assetPercent: number;
  price: number;
  instrumentId1: string;
  instrumentId2: string;
  instrumentName1: string;
  instrumentName2: string;
  ratio1: string;
  ratio2: string;
  contractSize1: string;
  contractSize2: string;
  orderSide1: any;
  orderSide2: any;
  involvedFund: string;
}

export interface IOptionDomainOrderbooksBody {
  insIds: {
    insId: string;
    ratio: number;
    side: StepOrderSide;
  }[];
}

export interface ISelectedTableInfo extends IOptionDomainOrderbooksBody {
  id: number;
}

export interface IOptionDomainPairPositionChart {
  graphPoints: Graph_Dto[];
  name: string;
}

export interface IChartCommonSearchParam {
  fromDate: string;
  toDate: string;
}

export interface IPairPositionFilterSearchParams {
  positionType: string[];
  groupId: number[];
  remainingOptionId: number;
  sameKind: boolean;
  sameStrikePrice: boolean;
  ins2: string;
  ins1: string;
  baseIns2: string;
  baseIns1: string;
  maxProfitLock: number;
  minProfitLock: number;
  maxReHedgeRatio: number;
  minLastDayTradeVolume: number;
  minLastDayTradeValue: number;
  lessMinRemainingDay: number;
  lessMaxRemainingDay: number;
  moreMinRemainingDay: number;
  moreMaxRemainingDay: number;
  minHedgeFactor: number;
  maxHedgeFactor: number;
  side1MinPrice: string;
  side2MinPrice: string;
  side1MinBasePricePercentage: string;
  side2MinBasePricePercentage: string;
  minGamma: string;
  maxGamma: string;
}
